import axios from 'axios';
import { getApiBaseUrl } from '../../core/config/api';

export interface ServerTimeResponse {
  server_time: string; // ISO 8601 format
  timezone: string;
  offset: number; // Offset en secondes par rapport à UTC
}

class ServerTimeService {
  private static instance: ServerTimeService;
  private serverTime: Date | null = null;
  private clientTime: Date | null = null;
  private offset: number = 0; // Différence en millisecondes entre serveur et client
  private lastSync: Date | null = null;
  private syncInterval: NodeJS.Timeout | null = null;

  private constructor() {}

  public static getInstance(): ServerTimeService {
    if (!ServerTimeService.instance) {
      ServerTimeService.instance = new ServerTimeService();
    }
    return ServerTimeService.instance;
  }

  /**
   * Synchronise l'heure avec le serveur
   */
  async syncServerTime(): Promise<boolean> {
    try {
      const clientTimeBeforeRequest = new Date();
      
      const response = await axios.get<ServerTimeResponse>(
        `${getApiBaseUrl(true)}/server/time`,
        {
          timeout: 5000,
        }
      );

      const clientTimeAfterRequest = new Date();
      const networkLatency = (clientTimeAfterRequest.getTime() - clientTimeBeforeRequest.getTime()) / 2;
      
      if (response.data && response.data.server_time) {
        this.serverTime = new Date(response.data.server_time);
        this.clientTime = new Date(clientTimeBeforeRequest.getTime() + networkLatency);
        
        // Calculer l'offset en tenant compte de la latence réseau
        this.offset = this.serverTime.getTime() - this.clientTime.getTime();
        this.lastSync = new Date();
        
        return true;
      }
      
      return false;
    } catch (error) {
      console.error('Erreur lors de la synchronisation de l\'heure serveur:', error);
      return false;
    }
  }

  /**
   * Obtient l'heure actuelle du serveur (calculée)
   */
  getServerTime(): Date {
    if (!this.serverTime || !this.clientTime) {
      // Si pas encore synchronisé, retourner l'heure locale
      return new Date();
    }
    
    // Calculer l'heure serveur basée sur l'offset et l'heure actuelle du client
    const now = new Date();
    const elapsed = now.getTime() - this.clientTime.getTime();
    return new Date(this.serverTime.getTime() + elapsed);
  }

  /**
   * Obtient l'offset entre l'heure serveur et l'heure client (en millisecondes)
   */
  getOffset(): number {
    return this.offset;
  }

  /**
   * Vérifie si l'heure est synchronisée
   */
  isSynced(): boolean {
    return this.serverTime !== null && this.lastSync !== null;
  }

  /**
   * Obtient la date de la dernière synchronisation
   */
  getLastSync(): Date | null {
    return this.lastSync;
  }

  /**
   * Démarre la synchronisation automatique périodique
   */
  startAutoSync(intervalMs: number = 60000): void {
    this.stopAutoSync();
    this.syncInterval = setInterval(() => {
      this.syncServerTime();
    }, intervalMs);
  }

  /**
   * Arrête la synchronisation automatique
   */
  stopAutoSync(): void {
    if (this.syncInterval) {
      clearInterval(this.syncInterval);
      this.syncInterval = null;
    }
  }

  /**
   * Compare l'heure serveur avec l'heure locale du joueur
   * Retourne la différence en millisecondes (positive = serveur en avance)
   */
  compareWithClientTime(): number {
    const serverTime = this.getServerTime();
    const clientTime = new Date();
    return serverTime.getTime() - clientTime.getTime();
  }

  /**
   * Formate l'heure serveur pour l'affichage
   */
  formatServerTime(format: 'full' | 'time' | 'date' = 'full'): string {
    const serverTime = this.getServerTime();
    const options: Intl.DateTimeFormatOptions = {
      timeZone: 'Europe/Paris',
    };

    switch (format) {
      case 'time':
        options.hour = '2-digit';
        options.minute = '2-digit';
        options.second = '2-digit';
        break;
      case 'date':
        options.year = 'numeric';
        options.month = '2-digit';
        options.day = '2-digit';
        break;
      case 'full':
      default:
        options.year = 'numeric';
        options.month = '2-digit';
        options.day = '2-digit';
        options.hour = '2-digit';
        options.minute = '2-digit';
        options.second = '2-digit';
        break;
    }

    const formatted = serverTime.toLocaleString('fr-FR', options);
    return `${formatted} FR`;
  }
}

export default ServerTimeService;

